import { DrawDeadlineConfig, GameConfig } from '@/config/game';
import { ID_TOTO_AW, ID_TOTO_EW } from '@/config/game/ids';
import apiRequest from '@/helpers/apiRequest';
import {
  fromTimestamp,
  getDateParseFormat,
  getNow,
  getWeekDayName,
} from '@/utilities/dateUtils';
import formatMillionCurrency from '@/utilities/formatMillionCurrency';
import { DateTime, WeekdayNumbers } from 'luxon';
import { getTotoJackpot } from './getTotoJackpot';

interface ApiData {
  jackpot?: number | null;
  jackpotInMio?: number;
  jackpotInEurFormatted?: string;
  datum: number;
  [key: string]: any;
}
interface ApiDataClass2New {
  jackpotNew?: number | null;
  jackpotClass2New?: number | null;
}

interface NextDrawDay {
  prefix: string;
  day: string;
  time: string;
  expire: number;
}

export interface NextJackpot extends NextDrawDay {
  id: number;
  value: number | string;
  valueClass1: number;
  valueClass2: number;
  label: string;
  labelGame: string;
}

export interface JackpotData {
  [game: string]: NextJackpot | null;
}

function formatDataInEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

export async function getNextJackpot(
  game: GameConfig
): Promise<NextJackpot | null> {
  const now = getNow();

  if (!game.drawDeadlines || game.drawDeadlines.length === 0) {
    return null;
  }

  const nextDrawDay = getNextDrawDay(game.drawDeadlines, now);
  const nextJackpot: NextJackpot = {
    id: game.id,
    prefix: nextDrawDay.prefix,
    day: nextDrawDay.day,
    time: nextDrawDay.time,
    value: 0,
    valueClass1: 0,
    valueClass2: 0,
    label: game.jackpotDesc,
    labelGame: game.jackpotDescGame || game.jackpotDesc,
    expire: nextDrawDay.expire,
  };

  if (game.id === ID_TOTO_EW || game.id === ID_TOTO_AW) {
    const totoJackpot = await getTotoJackpot(game);
    if (totoJackpot) {
      nextJackpot.expire = totoJackpot.deadline;
      nextJackpot.valueClass1 = totoJackpot.jackpotClass1;
      nextJackpot.valueClass2 = totoJackpot.jackpotClass2;
      if (totoJackpot.jackpot != null) {
        nextJackpot.label = formatDataInEuro(totoJackpot.jackpot);
        nextJackpot.labelGame = nextJackpot.label;
      }
    }
    return nextJackpot;
  }

  if (game.jackpotSrc) {
    try {
      const data: ApiData | null = await apiRequest(game.jackpotSrc);
      if (
        data &&
        (data.jackpot || data.jackpotInMio || data.jackpotInEurFormatted) &&
        apiIsUpToDate(data, game.drawDeadlines)
      ) {
        const value = data.jackpot || data.jackpotInMio || 0;
        nextJackpot.label =
          value === 0 && data.jackpotInEurFormatted
            ? `${data.jackpotInEurFormatted} €`
            : game.jackpotIsEurUnFormatted
              ? formatDataInEuro(value)
              : formatMillionCurrency(value);
        nextJackpot.labelGame = nextJackpot.label;
        nextJackpot.value = value;
        nextJackpot.valueClass1 = data.jackpot || 0;

        if (game.jackpotLast) {
          const dataClass2New: ApiDataClass2New | null = await apiRequest(
            game.jackpotLast
          );
          if (dataClass2New != null && dataClass2New.jackpotClass2New != null) {
            const valueClass2 = dataClass2New.jackpotClass2New || 0;
            nextJackpot.valueClass2 = valueClass2;
            if (dataClass2New != null && dataClass2New.jackpotNew != null) {
              const valueClass1 = dataClass2New.jackpotNew || 0;
              nextJackpot.valueClass1 = valueClass1;
            }
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }
  return nextJackpot;
}

export function getNextDrawDay(
  deadlines: readonly DrawDeadlineConfig[],
  now = getNow()
): NextDrawDay {
  const available = deadlines
    .map((item) => getDateFromDayAndTime(item.day, item.time, now))
    .sort((a, b) => a.toMillis() - b.toMillis());
  const nextThisWeek = available.find(
    (item: DateTime) =>
      item.startOf('day').toMillis() >= now.startOf('day').toMillis() &&
      item.toFormat('WW') === now.toFormat('WW')
  );
  const nextDrawDay = nextThisWeek || available[0];
  return {
    prefix: nextThisWeek ? 'Diesen' : 'Nächsten',
    day: getWeekDayName(nextDrawDay.weekday - 1),
    time: nextDrawDay.toFormat(getDateParseFormat('Time')),
    expire: nextDrawDay.toMillis(),
  };
}

function getLastDrawDay(
  deadlines: readonly DrawDeadlineConfig[],
  now = getNow()
) {
  const available = deadlines
    .map((item) => getDateFromDayAndTime(item.day, item.time, now))
    .map((item) => {
      if (item.startOf('day').toMillis() >= now.startOf('day').toMillis()) {
        const weekNumber = item.get('weekNumber');
        item = item.set({ weekNumber: weekNumber - 1 });
      }
      return item;
    })
    .sort((a, b) => b.toMillis() - a.toMillis());
  return available[0];
}

function apiIsUpToDate(
  data: ApiData,
  deadlines: readonly DrawDeadlineConfig[]
): boolean {
  const lastDraw = getLastDrawDay(deadlines);

  const dateDraw = fromTimestamp(data.datum);
  return (
    lastDraw.startOf('day').toMillis() === dateDraw?.startOf('day').toMillis()
  );
}

export const toDayNumber = (dayString) => {
  switch (dayString) {
    case 'Montag':
      return 1;
    case 'Dienstag':
      return 2;
    case 'Mittwoch':
      return 3;
    case 'Donnerstag':
      return 4;
    case 'Freitag':
      return 5;
    case 'Samstag':
      return 6;
    case 'Sonntag':
      return 7;
    default:
      throw new Error(`unknown dayString ${dayString}`);
  }
};

function getDateFromDayAndTime(
  day: string,
  time: string,
  now: DateTime
): DateTime {
  // Ziehungstag
  const dayNumber: WeekdayNumbers = toDayNumber(day);
  let date = DateTime.fromMillis(now.toMillis(), { zone: 'Europe/Berlin' });
  const [hours, minutes] = time.split(':').map((str) => parseInt(str));
  date = date.set({ hour: hours, minute: minutes, second: 0 });
  const weekNumber = now.get('weekNumber');
  if (now.weekday <= dayNumber) {
    date = date.set({ weekday: dayNumber });
    if (date.toMillis() <= now.toMillis()) {
      date = date.set({ weekNumber: weekNumber + 1, weekday: dayNumber });
    }
  } else {
    date = date.set({ weekNumber: weekNumber + 1, weekday: dayNumber });
  }
  return date;
}
